/**
 * React Query Configuration
 *
 * Shared configuration for React Query across the application.
 * Provides consistent settings for caching, retries, and error handling.
 */

/**
 * Base filter type used across query keys
 * This is intentionally loose to allow service-specific filter types
 */
export type QueryFilters = object;

/**
 * Query key factory for consistent key generation
 *
 * @example
 * ```ts
 * // For a specific resource
 * queryKeys.files.list(matterId, filters)
 * queryKeys.files.item(fileId)
 *
 * // For invalidation
 * queryClient.invalidateQueries({ queryKey: queryKeys.files.all })
 * queryClient.invalidateQueries({ queryKey: queryKeys.files.list(matterId) })
 * ```
 */
export const queryKeys = {
  // Files service
  files: {
    all: ['files'] as const,
    lists: () => ['files', 'list'] as const,
    list: (matterId: number, filters?: QueryFilters) =>
      ['files', 'list', matterId, filters] as const,
    items: () => ['files', 'item'] as const,
    item: (fileId: number) => ['files', 'item', fileId] as const
  },

  // Matters service
  matters: {
    all: ['matters'] as const,
    lists: () => ['matters', 'list'] as const,
    list: (filters?: QueryFilters) => ['matters', 'list', filters] as const,
    items: () => ['matters', 'item'] as const,
    item: (matterId: number) => ['matters', 'item', matterId] as const
  },

  // Institutions service
  institutions: {
    all: ['institutions'] as const,
    lists: () => ['institutions', 'list'] as const,
    list: (matterId: number) => ['institutions', 'list', matterId] as const,
    items: () => ['institutions', 'item'] as const,
    item: (institutionId: number) =>
      ['institutions', 'item', institutionId] as const
  },

  // Tasks service
  tasks: {
    all: ['tasks'] as const,
    lists: () => ['tasks', 'list'] as const,
    list: (matterId: number, filters?: QueryFilters) =>
      ['tasks', 'list', matterId, filters] as const,
    items: () => ['tasks', 'item'] as const,
    item: (taskId: number) => ['tasks', 'item', taskId] as const
  },

  // Contacts service
  contacts: {
    all: ['contacts'] as const,
    lists: () => ['contacts', 'list'] as const,
    list: (matterId: number, filters?: QueryFilters) =>
      ['contacts', 'list', matterId, filters] as const,
    items: () => ['contacts', 'item'] as const,
    item: (contactId: number) => ['contacts', 'item', contactId] as const
  },

  // Inventory service
  inventory: {
    all: ['inventory'] as const,
    lists: () => ['inventory', 'list'] as const,
    list: (matterId: number, filters?: QueryFilters) =>
      ['inventory', 'list', matterId, filters] as const,
    items: () => ['inventory', 'item'] as const,
    item: (itemId: number) => ['inventory', 'item', itemId] as const
  }

  // Add more services as they are migrated to React Query
} as const;
